import * as React from 'react';
import { useStyles } from '../../style/Pages/BasicCompanyInfo';
import { Box } from '@mui/system';
import { useFormik } from 'formik';
import { Button, Grid, Typography, MenuItem, IconButton, CircularProgress } from '@mui/material';
import { schemaValidationGeneralCompanyInfo, schemaValidationGeneralCompanyInfoType } from '../../utils/validation';
import { InputField } from '../../components/InputController';
import { useDispatch, useSelector } from 'react-redux';
import { clearOnboardingApplicationFlags, getPmDetails, getUpdateOnboardingApplication, selectOnboardingApplicationState } from './sagas/pmonboardingSlice';
import { useHistory } from 'react-router-dom';
import CustomDropdown from '../../components/customDropdown';
import { approvedISOStandards } from '../../shared/util/approvedISOStandards';
import BackIcon from '../../icons/backIcon.svg';
import { PhoneNumberInput } from '../../shared/PhoneNumberInput';

export const GeneralCompanyInfo = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;

  const { pmDetails, onboarding, isFetching, isSuccess } = useSelector(selectOnboardingApplicationState);

  //General Company Info Saved Values
  const [savedValues, setSavedValues] = React.useState<schemaValidationGeneralCompanyInfoType>({
    company_legal_name: '',
    company_email: '',
    phone_number: '',
    company_physical_address_line1: '',
    city: '',
    state: approvedStates[0].Code,
    zip: '',
    country: 'US',
    website: '',
  });

  // only intialize the form after data has been fetched from the api
  React.useEffect(() => {
    onboarding?.LegalName &&
      setSavedValues({
        ...savedValues,
        company_legal_name: onboarding?.LegalName!,
        company_email: onboarding?.Email!,
        phone_number: onboarding?.PhoneNumber!,
        company_physical_address_line1: onboarding?.PhysicalAddress?.Line1!,
        city: onboarding?.PhysicalAddress?.City!,
        state: onboarding?.PhysicalAddress?.State!,
        zip: onboarding?.PhysicalAddress?.Zip!,
        country: onboarding?.PhysicalAddress?.Country!,
        website: onboarding?.Website!,
      });
  }, [onboarding]);

  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);

  const formik = useFormik<schemaValidationGeneralCompanyInfoType>({
    initialValues: savedValues,
    validationSchema: schemaValidationGeneralCompanyInfo,
    onSubmit: async (values) => {
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: onboarding?.Uuid || pmDetails?.Company?.Uuid,
          data: {
            LegalName: values.company_legal_name,
            Website: values.website,
            PhoneNumber: values.phone_number,
            Email: values.company_email,
            PhysicalAddress: {
              Line1: values.company_physical_address_line1,
              City: values.city,
              Country: values.country,
              State: values.state,
              Zip: values.zip,
            },
          },
        },
      });
      setIsFormSubmitted(true);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  React.useEffect(() => {
    if (isSuccess) {
      if (isFormSubmitted) history.push('/onboarding/applicationwizard');

      // Clear all onboarding flags
      dispatch({
        type: clearOnboardingApplicationFlags.type,
        payload: {},
      });
    }
  }, [isFormSubmitted, isSuccess]);

  React.useEffect(() => {
    // Clear all onboarding flags
    dispatch({
      type: clearOnboardingApplicationFlags.type,
      payload: {},
    });

    // fetch company uuid if onboarding state has been erased, probably due to a refresh
    !onboarding?.Uuid &&
      dispatch({
        type: getPmDetails.type,
        payload: {},
      });
  }, []);

  React.useEffect(() => {
    // fetch onboarding application if it doesnt exist in app context
    pmDetails.Company?.Uuid &&
      !onboarding?.Uuid &&
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: pmDetails.Company.Uuid,
          data: {},
        },
      });
  }, [pmDetails]);

  const [selectedState, setSelectedState] = React.useState(approvedStates[0].Code);
  const resetStateSelection = () => {
    formik.setFieldValue('state', '');
    formik.setFieldValue('state_of_incorporation', '');
    setSelectedState('');
  };

  // load selected state if a valid selection was made originally, else let plain text box render state of origin
  React.useEffect(() => {
    formik.values.country &&
      ['US', 'CA'].includes(formik.values.country) &&
      approvedStates.map((stateRecord) => stateRecord.Code).includes(formik.values.state) &&
      setSelectedState(formik.values.state);
  }, [onboarding, formik]);

  const handleClickFinishLater = () => {
    history.push('/onboarding/applicationwizard');
  };

  return (
    <Grid item xs={11} sm={9} lg={8} xl={7}>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <IconButton sx={{ p: 1 }} onClick={handleClickFinishLater}>
          <img src={BackIcon} alt="cancel icon" width="17px" height="17px" />
        </IconButton>
        <Typography
          style={{
            fontWeight: 600,
            fontSize: '18px',
          }}
          sx={{ p: 1 }}
          className={classes.companyInfo}
        >
          General Company Info
        </Typography>
      </Box>

      <Box component="form" noValidate onSubmit={formik.handleSubmit} className={classes.centerLabel}>
        <InputField
          name={'company_legal_name'}
          label={'Company Name'}
          isboxShadow={true}
          value={formik.values.company_legal_name || ''}
          isError={false}
          errors={formik.errors?.company_legal_name}
          touched={formik.touched?.company_legal_name}
          onBlur={formik.handleBlur}
          onChange={(e) => formik.setFieldValue('company_legal_name', e.target?.value)}
        />
        <InputField
          name={'company_email'}
          isboxShadow={true}
          label={'Company Email'}
          value={formik.values.company_email || ''}
          isError={false}
          errors={formik.errors?.company_email}
          touched={formik.touched?.company_email}
          onBlur={formik.handleBlur}
          onChange={(e) => formik.setFieldValue('company_email', e.target?.value)}
        />
        <PhoneNumberInput
          {...formik.getFieldProps('phone_number')}
          label="Phone Number"
          showErrorMessage={!!formik.errors?.phone_number && !!formik.touched?.phone_number}
          onChange={(phone) => {
            formik.setFieldValue('phone_number', phone.startsWith('+') ? phone : `+${phone}`);
          }}
          errorMessage={formik.errors?.phone_number}
        />
        {/*Adding margins to phoneinput above is making it wierd so adding it here, do not remove */}
        <Box sx={{ marginTop: '16px' }}></Box>
        <InputField
          name={'website'}
          isboxShadow={true}
          label={'Website'}
          value={formik.values.website || ''}
          isError={false}
          errors={formik.errors?.website}
          touched={formik.touched?.website}
          onBlur={formik.handleBlur}
          onChange={(e) => formik.setFieldValue('website', e.target?.value)}
        />
        <InputField
          name={'company_physical_address_line1'}
          isboxShadow={true}
          label={'Company Physical Address'}
          value={formik.values.company_physical_address_line1 || ''}
          isError={false}
          errors={formik.errors?.company_physical_address_line1}
          touched={formik.touched?.company_physical_address_line1}
          onBlur={formik.handleBlur}
          onChange={(e) => formik.setFieldValue('company_physical_address_line1', e.target?.value)}
        />

        <Grid container spacing={1} direction="row">
          <Grid item md={6}>
            <InputField
              name={'city'}
              isboxShadow={true}
              label={'City'}
              value={formik.values.city || ''}
              isError={false}
              errors={formik.errors?.city}
              touched={formik.touched?.city}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('city', e.target?.value)}
            />
          </Grid>
          <Grid item md={6}>
            {formik.values.country && ['US', 'CA'].includes(formik.values.country) ? (
              <>
                <CustomDropdown
                  id={'state-dropdown'}
                  isFullWidth
                  label={'State / Province'}
                  labelId={''}
                  value={selectedState}
                  handleChange={(e) => {
                    setSelectedState(e.target?.value);
                    formik.setFieldValue('state', e.target?.value);
                  }}
                >
                  {formik.values.country === 'US'
                    ? approvedStates.map((stateRecord) => (
                        <MenuItem style={{ height: '36px' }} key={stateRecord.Code} value={stateRecord.Code}>
                          {stateRecord.State + ' - ' + stateRecord.Code}
                        </MenuItem>
                      ))
                    : approvedProvinces.map((provinceRecord) => (
                        <MenuItem style={{ height: '36px' }} key={provinceRecord.Code} value={provinceRecord.Code}>
                          {provinceRecord.Province + ' - ' + provinceRecord.Code}
                        </MenuItem>
                      ))}{' '}
                </CustomDropdown>
              </>
            ) : (
              <InputField
                value={formik.values.state || ''}
                name={'state'}
                isError={false}
                errors={formik.errors?.state}
                touched={formik.touched?.state}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('state', e.target?.value)}
                placeholder={'State / Province'}
                label={'State / Province'}
              />
            )}
          </Grid>
        </Grid>
        <Grid container spacing={1} direction="row">
          <Grid item md={6}>
            <InputField
              name={'zip'}
              label={'Postal Code'}
              value={formik.values.zip || ''}
              isError={false}
              errors={formik.errors?.zip}
              touched={formik.touched?.zip}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('zip', e.target?.value)}
            />
          </Grid>
          <Grid item md={6}>
            <CustomDropdown
              label={'Country'}
              isFullWidth
              handleChange={(e: any) => {
                resetStateSelection();

                // update selected country
                formik.setFieldValue('country', e.target?.value);
              }}
              value={formik.values.country || ''}
            >
              {approvedCountries.map((countryRecord) => (
                <MenuItem style={{ height: '36px' }} key={countryRecord.Code} value={countryRecord.Code}>
                  {countryRecord.Country + ' - ' + countryRecord.Code}
                </MenuItem>
              ))}
            </CustomDropdown>
          </Grid>
        </Grid>

        <Box style={{ width: '100%' }} sx={{ display: 'flex', justifyContent: 'end' }}>
          <div className={classes.wrapper}>
            <Button type="submit" disabled={Boolean(!formik.isValid)} className={classes.signUpBtn}>
              <Typography className={classes.signUpBtnText} sx={{ margin: '0 10px' }}>
                Save
              </Typography>
            </Button>
            {isFormSubmitted && isFetching && <CircularProgress size={26} className={classes.buttonProgress} />}
          </div>
        </Box>
      </Box>
    </Grid>
  );
};
